import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Music, Play, Pause, X, Volume2, VolumeX, Loader2, Download } from "lucide-react";

const SONG_URL = "/audio/Es%20tiempo%20de%20creer.mp3";
const SONG_TITLE = "Es Tiempo de Creer";
const SONG_SUBTITLE = "La canción del movimiento · Frente Ya";

const formatTime = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
};

export const SongButton = () => {
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState("");
  const audioRef = useRef(null);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setProgress(a.currentTime);
    const onMeta = () => setDuration(a.duration);
    const onEnd = () => setPlaying(false);
    const onPlay = () => { setPlaying(true); setBuffering(false); };
    const onPause = () => setPlaying(false);
    const onWaiting = () => setBuffering(true);
    const onCanPlay = () => setBuffering(false);
    const onError = () => {
      setError("No se pudo cargar la canción.");
      setPlaying(false);
      setBuffering(false);
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("waiting", onWaiting);
    a.addEventListener("canplay", onCanPlay);
    a.addEventListener("error", onError);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("waiting", onWaiting);
      a.removeEventListener("canplay", onCanPlay);
      a.removeEventListener("error", onError);
    };
  }, []);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    setError("");
    if (!a.paused) {
      a.pause();
      return;
    }
    setBuffering(true);
    try {
      await a.play();
    } catch (err) {
      setBuffering(false);
      setPlaying(false);
      setError("No se pudo reproducir. Probá de nuevo.");
      console.warn("[SongButton] play() rejected:", err);
    }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.min(1, Math.max(0, x / rect.width));
    a.currentTime = ratio * duration;
  };

  const toggleMute = () => {
    const a = audioRef.current;
    if (!a) return;
    a.muted = !a.muted;
    setMuted(a.muted);
  };

  return (
    <>
      <audio ref={audioRef} src={SONG_URL} preload="none" />

      <AnimatePresence>
        {!open && (
          <motion.button
            key="fab"
            onClick={() => setOpen(true)}
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="fy-heartbeat fy-ring-pulse fixed bottom-5 right-5 z-40 group bg-[#0A192F] hover:bg-[#0A2463] text-white shadow-2xl px-5 py-3.5 flex items-center gap-3 font-headings uppercase tracking-wide text-sm border-2 border-[#F5B400]"
            data-testid="song-fab"
            style={{ borderRadius: 0 }}
          >
            <span className="relative flex items-center justify-center">
              <span className="relative w-8 h-8 rounded-full bg-[#F5B400] text-[#0A192F] flex items-center justify-center">
                {playing ? <Pause size={14} fill="currentColor" /> : <Music size={14} />}
              </span>
            </span>
            <span className="flex flex-col items-start leading-tight">
              <span className="text-[10px] tracking-[0.25em] text-[#F5B400]">Escuchá</span>
              <span className="text-sm">nuestra canción</span>
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            key="player"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-5 right-5 z-40 w-[340px] max-w-[calc(100vw-2.5rem)] bg-[#0A192F] text-white shadow-2xl border-2 border-[#F5B400]"
            data-testid="song-player"
          >
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <span className="font-headings text-[10px] uppercase tracking-[0.25em] text-[#F5B400]">
                Canción del movimiento
              </span>
              <button
                onClick={() => setOpen(false)}
                className="text-white/60 hover:text-white"
                aria-label="Cerrar reproductor"
                data-testid="song-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-4 pb-3">
              <div className="font-headings text-xl font-bold uppercase tracking-tight leading-tight">
                {SONG_TITLE}
              </div>
              <div className="text-xs text-white/60 mt-0.5">{SONG_SUBTITLE}</div>
            </div>

            <div className="px-4">
              <div
                onClick={seek}
                className="relative h-1.5 bg-white/15 cursor-pointer group"
                data-testid="song-progress-bar"
              >
                <div
                  className="absolute inset-y-0 left-0 bg-[#F5B400]"
                  style={{ width: duration ? `${(progress / duration) * 100}%` : "0%" }}
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-[#F5B400] rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ left: duration ? `calc(${(progress / duration) * 100}% - 6px)` : "-6px" }}
                />
              </div>
              <div className="flex items-center justify-between mt-1.5 font-headings text-[10px] tabular-nums text-white/60">
                <span>{formatTime(progress)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {error && (
              <div className="px-4 pt-2 text-xs text-red-300" data-testid="song-error">
                {error}
              </div>
            )}

            <div className="px-4 pb-4 pt-2 flex items-center justify-between gap-3">
              <button
                onClick={toggle}
                disabled={buffering}
                className="flex-1 bg-[#F5B400] hover:bg-[#d99f00] disabled:opacity-70 text-[#0A192F] font-headings uppercase tracking-wide text-sm py-3 inline-flex items-center justify-center gap-2 transition-colors"
                data-testid="song-toggle"
              >
                {buffering ? (
                  <>
                    <Loader2 className="animate-spin" size={16} /> Cargando…
                  </>
                ) : playing ? (
                  <>
                    <Pause size={16} fill="currentColor" /> Pausar
                  </>
                ) : (
                  <>
                    <Play size={16} fill="currentColor" /> Reproducir
                  </>
                )}
              </button>
              <button
                onClick={toggleMute}
                className="w-11 h-11 border border-white/20 hover:border-[#F5B400] text-white/80 hover:text-[#F5B400] inline-flex items-center justify-center transition-colors"
                aria-label={muted ? "Activar sonido" : "Silenciar"}
                data-testid="song-mute"
              >
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <a
                href={SONG_URL}
                download="Es tiempo de creer.mp3"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 border border-white/20 hover:border-[#F5B400] text-white/80 hover:text-[#F5B400] inline-flex items-center justify-center transition-colors"
                aria-label="Descargar canción"
                data-testid="song-download"
                title="Descargar canción"
              >
                <Download size={16} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SongButton;
